import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import EmployeeDetailDrawer from '../EmployeeDetailDrawer/EmployeeDetailDrawer';
import DeleteEmployeeModal from '../DeleteEmployeeModal/DeleteEmployeeModal';
import { checkPermission, displayEmployeePassword, permissionType } from '../../../app/utils/common/Functions';
import EditIcon from "../../../assets/icon/edit.png"
import moment from "moment"
import './EmployeesContainer.scss'
import { deleteEmployee, updateAttendanceCodeAPI, updateEmployeeCodeAPI } from '../../../Redux/Apis/employeeApi';

const EmployeesContainer = ({ profiles, variant = "active" }) => {
    const dispatch = useDispatch();
    const deleteLoading = useSelector((state) => state.employee?.loading);
    const isExEmployeeView = variant === "ex";
    const showPasswordColumn =
        !isExEmployeeView && checkPermission(permissionType.USER_MANAGEMENT, "update");
    const canUpdate =
        !isExEmployeeView && checkPermission(permissionType.USER_MANAGEMENT, "update");
    const canDelete =
        !isExEmployeeView && checkPermission(permissionType.USER_MANAGEMENT, "delete");
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState();
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [attendanceCodeMap, setAttendanceCodeMap] = useState({});
    const [employeeCodeMap, setEmployeeCodeMap] = useState({});

    const toggleDetailDrawer = () => {
        setIsEditOpen(!isEditOpen)
    }

    const getAttendanceValue = (profile) =>
        attendanceCodeMap[profile?.userId] ?? profile?.attendanceEmployeeCode ?? "";

    const saveAttendanceCode = (profile) => {
        const value = getAttendanceValue(profile).trim();
        dispatch(updateAttendanceCodeAPI({ id: profile?.userId, attendanceEmployeeCode: value }));
    };

    const getEmployeeCodeValue = (profile) =>
        employeeCodeMap[profile?.userId] ?? profile?.employeeCode ?? "";

    const saveEmployeeCode = (profile) => {
        const value = getEmployeeCodeValue(profile).trim();
        dispatch(updateEmployeeCodeAPI({ id: profile?.userId, employeeCode: value }));
    };

    const renderEmployeeCodeField = (profile, layout = "table") => (
        <div className={`attendance-code-field attendance-code-field--${layout}`}>
            <div className="attendance-code-field__controls">
                <input
                    className="attendance-code-input"
                    placeholder="e.g. TCS001"
                    value={getEmployeeCodeValue(profile)}
                    onChange={(e) => {
                        const value = e.target.value;
                        setEmployeeCodeMap((prev) => ({ ...prev, [profile?.userId]: value }));
                    }}
                />
                <button
                    className="attendance-save-btn"
                    type="button"
                    onClick={() => saveEmployeeCode(profile)}
                >
                    Save
                </button>
            </div>
            <div className="attendance-map-status-wrap">
                {getEmployeeCodeValue(profile).trim().length > 0 ? (
                    <span className="attendance-map-badge mapped">Set</span>
                ) : (
                    <span className="attendance-map-badge unmapped">Not set</span>
                )}
            </div>
        </div>
    );

    const renderAttendanceCodeField = (profile, layout = "table") => (
        <div className={`attendance-code-field attendance-code-field--${layout}`}>
            <div className="attendance-code-field__controls">
                <input
                    className="attendance-code-input"
                    placeholder="e.g. ESSL001"
                    value={getAttendanceValue(profile)}
                    onChange={(e) => {
                        const value = e.target.value;
                        setAttendanceCodeMap((prev) => ({ ...prev, [profile?.userId]: value }));
                    }}
                />
                <button
                    className="attendance-save-btn"
                    type="button"
                    onClick={() => saveAttendanceCode(profile)}
                >
                    Save
                </button>
            </div>
            <div className="attendance-map-status-wrap">
                {getAttendanceValue(profile).trim().length > 0 ? (
                    <span className="attendance-map-badge mapped">Mapped</span>
                ) : (
                    <span className="attendance-map-badge unmapped">Unmapped</span>
                )}
            </div>
        </div>
    );

    const openEditDrawer = (profile) => {
        setSelectedProfile(profile);
        setIsEditOpen(true);
    };

    const handleDelete = (profile) => {
        setEmployeeToDelete(profile);
    };

    const handleConfirmDelete = ({ userId, lastWorkingDate }) => {
        if (!userId) return;
        dispatch(deleteEmployee({ userId, lastWorkingDate }))
            .unwrap()
            .then(() => setEmployeeToDelete(null))
            .catch(() => { });
    };

    const formatDate = (value) => (value ? moment(value).format("DD MMM, YYYY") : "-");

    const fullName = (profile) =>
        `${profile?.firstname || ""} ${profile?.lastname || ""}`.trim();

    return (
        <>
            <div className="employees-container">
                <div className="employees-table-wrap">
                    <table className="table-default employees-table w-full">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Name</th>
                                <th>Email</th>
                                {showPasswordColumn && <th>Password</th>}
                                <th>Mobile</th>
                                <th>Employee Code</th>
                                <th>Designation</th>
                                {canUpdate && <th>Attendance Code</th>}
                                <th>DOB</th>
                                <th>Work anniversary</th>
                                {isExEmployeeView && <th>Last working day</th>}
                                <th>Position</th>
                                {canUpdate && <th>Edit</th>}
                                {canDelete && <th>Delete</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {profiles.map((profile) => (
                                <tr key={profile.userId}>
                                    <td>
                                        <div className="employee-avatar-wrap">
                                            <img
                                                className="employee-avatar rounded-full object-cover"
                                                src={profile?.profileImage}
                                                alt={fullName(profile) || "profile"}
                                            />
                                        </div>
                                    </td>
                                    <td>{fullName(profile)}</td>
                                    <td>{profile?.email}</td>
                                    {showPasswordColumn && (
                                        <td>{displayEmployeePassword(profile?.password, profile)}</td>
                                    )}
                                    <td>{profile?.phoneNumber || "-"}</td>
                                    <td>
                                        {canUpdate
                                            ? renderEmployeeCodeField(profile, "table")
                                            : profile?.employeeCode || "—"}
                                    </td>
                                    <td>{profile?.designation || "—"}</td>
                                    {canUpdate && <td>{renderAttendanceCodeField(profile, "table")}</td>}
                                    <td>{formatDate(profile?.dob)}</td>
                                    <td>{formatDate(profile?.workAnniversaryDate)}</td>
                                    {isExEmployeeView && (
                                        <td>{formatDate(profile?.lastWorkingDate)}</td>
                                    )}
                                    <td className="capitalize">{profile?.role || "-"}</td>
                                    {canUpdate && (
                                        <td>
                                            <div className="employee-action-cell">
                                                <button
                                                    type="button"
                                                    className="employee-edit-btn"
                                                    onClick={() => openEditDrawer(profile)}
                                                    aria-label={`Edit ${fullName(profile)}`}
                                                >
                                                    <img src={EditIcon} alt="" />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                    {canDelete && (
                                        <td>
                                            <div className="employee-action-cell">
                                                <button
                                                    type="button"
                                                    className="employee-delete-btn"
                                                    onClick={() => handleDelete(profile)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {!profiles.length && (
                                <tr>
                                    <td
                                        colSpan={
                                            8 +
                                            (showPasswordColumn ? 1 : 0) +
                                            (canUpdate ? 2 : 0) +
                                            (canDelete ? 1 : 0) +
                                            (isExEmployeeView ? 1 : 0)
                                        }
                                        className="employees-empty-row"
                                    >
                                        {isExEmployeeView
                                            ? "No ex-employees found."
                                            : "No employees found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="employees-cards" aria-label="Employee list">
                    {!profiles.length && (
                        <p className="employees-cards-empty">
                            {isExEmployeeView
                                ? "No ex-employees found."
                                : "No employees found."}
                        </p>
                    )}
                    {profiles.map((profile) => (
                        <article key={profile.userId} className="employee-card">
                            <header className="employee-card__header">
                                <img
                                    className="employee-card__avatar"
                                    src={profile?.profileImage}
                                    alt={fullName(profile) || "profile"}
                                />
                                <div className="employee-card__identity">
                                    <h3 className="employee-card__name">{fullName(profile)}</h3>
                                    <p className="employee-card__email">{profile?.email}</p>
                                    <span className="employee-card__role capitalize">{profile?.role || "-"}</span>
                                    {isExEmployeeView && (
                                        <span className="employee-card__ex-badge">Ex-employee</span>
                                    )}
                                </div>
                            </header>

                            <dl className="employee-card__details">
                                <div className="employee-card__detail">
                                    <dt>Mobile</dt>
                                    <dd>{profile?.phoneNumber || "-"}</dd>
                                </div>
                                {showPasswordColumn && (
                                    <div className="employee-card__detail">
                                        <dt>Password</dt>
                                        <dd>{displayEmployeePassword(profile?.password, profile)}</dd>
                                    </div>
                                )}
                                <div className="employee-card__detail">
                                    <dt>DOB</dt>
                                    <dd>{formatDate(profile?.dob)}</dd>
                                </div>
                                <div className="employee-card__detail">
                                    <dt>Work anniversary</dt>
                                    <dd>{formatDate(profile?.workAnniversaryDate)}</dd>
                                </div>
                                {isExEmployeeView && (
                                    <div className="employee-card__detail">
                                        <dt>Last working day</dt>
                                        <dd>{formatDate(profile?.lastWorkingDate)}</dd>
                                    </div>
                                )}
                                <div className="employee-card__detail">
                                    <dt>Designation</dt>
                                    <dd>{profile?.designation || "—"}</dd>
                                </div>
                            </dl>

                            <div className="employee-card__section">
                                <p className="employee-card__section-title">Employee Code</p>
                                {canUpdate ? (
                                    <>
                                        <p className="text-xs text-[#6f7d97] mb-2">Used on salary slips</p>
                                        {renderEmployeeCodeField(profile, "card")}
                                    </>
                                ) : (
                                    <p className="text-sm text-[#27314a]">{profile?.employeeCode || "—"}</p>
                                )}
                            </div>
                            {canUpdate && (
                                <div className="employee-card__section">
                                    <p className="employee-card__section-title">Attendance Code</p>
                                    {renderAttendanceCodeField(profile, "card")}
                                </div>
                            )}

                            {(canUpdate || canDelete) && (
                                <footer className="employee-card__actions">
                                    {canUpdate && (
                                        <button
                                            type="button"
                                            className="employee-card__edit-btn"
                                            onClick={() => openEditDrawer(profile)}
                                        >
                                            <img src={EditIcon} alt="" />
                                            Edit
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            type="button"
                                            className="employee-delete-btn employee-card__delete-btn"
                                            onClick={() => handleDelete(profile)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </footer>
                            )}
                        </article>
                    ))}
                </div>

                {isEditOpen && (
                    <EmployeeDetailDrawer
                        isCreate={false}
                        profile={selectedProfile}
                        isEditOpen={isEditOpen}
                        toggleDetailDrawer={toggleDetailDrawer}
                    />
                )}

                <DeleteEmployeeModal
                    isOpen={Boolean(employeeToDelete)}
                    profile={employeeToDelete}
                    onClose={() => {
                        if (!deleteLoading) setEmployeeToDelete(null);
                    }}
                    onConfirm={handleConfirmDelete}
                    confirmLoading={deleteLoading}
                />
            </div>
        </>
    )
}

export default EmployeesContainer
